import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { CardFrame, QueryBody } from '@/components/ui/card-frame';
import { plural } from '@/lib/plural';

import { coverageTone, formatPctEs } from '../presentation';
import type { NormCoverage } from '../types';
import { useNormCompliance } from '../use-dashboard';

// Cumplimiento por norma (so=430, spec §3.3; so=410 D6): % de cláusulas activas con un
// documento vigente y al día. Cada norma abre la Lista Maestra filtrada por ella, que es
// como se prepara una auditoría por requisito.

function uncoveredText(n: NormCoverage): string {
  return n.uncoveredClauses === 0
    ? 'todas las cláusulas cubiertas'
    : `${plural(n.uncoveredClauses, 'cláusula', 'cláusulas')} sin documento vigente`;
}

function NormRow({ norm }: { norm: NormCoverage }): ReactElement {
  const pct = formatPctEs(norm.coveragePct);
  // Solo vigentes: es lo que cuenta "documentos mapeados" (so=410 D6).
  const to = `/documentos?${new URLSearchParams({ norma: norm.normCode, estado: 'vigente' }).toString()}`;
  return (
    <Link to={to} className="rowb norm-row">
      <svg
        width="76"
        height="76"
        viewBox="0 0 42 42"
        role="img"
        aria-label={`${norm.normCode}: ${pct} % de cláusulas cubiertas`}
      >
        <circle cx="21" cy="21" r="15.9" fill="none" stroke="var(--bg-deep)" strokeWidth="5" />
        <circle
          cx="21"
          cy="21"
          r="15.9"
          fill="none"
          className={`ring-${coverageTone(norm.coveragePct)}`}
          strokeWidth="5"
          strokeDasharray={`${norm.coveragePct} 100`}
          strokeLinecap="round"
          transform="rotate(-90 21 21)"
        />
      </svg>
      <div>
        <div className="eyebrow norm-code">{norm.normCode}</div>
        <div className="display norm-pct">{pct} %</div>
        <div className="dash-sub">
          {plural(norm.mappedDocuments, 'documento mapeado', 'documentos mapeados')} ·{' '}
          {uncoveredText(norm)}
        </div>
      </div>
    </Link>
  );
}

export function NormComplianceCard({ companyId }: { companyId: string }): ReactElement {
  const query = useNormCompliance({ companyId, enabled: true });
  return (
    <CardFrame title="Cumplimiento por norma" count="cláusulas con documento vigente">
      <QueryBody
        query={query}
        rows={3}
        isEmpty={(d) => d.norms.length === 0}
        emptyText="No hay normas con cláusulas activas."
      >
        {(d) => (
          <div className="cb norm-list">
            {d.norms.map((n) => (
              <NormRow key={n.normCode} norm={n} />
            ))}
          </div>
        )}
      </QueryBody>
    </CardFrame>
  );
}
